import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * First-run disclosure screen. Shown ONCE after a new user finishes setup.
 * The single most important page in the app for store reviewers — it makes
 * Nia's role unambiguous.
 *
 * Once acknowledged we store `nia_disclosure_v1` in localStorage so it never
 * shows again. The user can re-read the full version at any time via
 * Profile → Legal → Local Agents disclosure.
 *
 * Phase 9 — Premium redesign:
 *   * Calm warm cream background — never alarming, no dark green.
 *   * Playfair Display "Before you start" headline in ink.
 *   * Clay-tint shield badge on top.
 *   * Bullets are subtle gold dots on ink text — Apple-safe and warm.
 *   * Acknowledge checkbox card uses sage tint to feel like consent.
 *   * Continue button is clay primary (per design system).
 */
const KEY = 'nia_disclosure_v1';

export default function FirstRunDisclosure() {
    useAuth(); // ensure auth state is ready before showing the disclosure
    const nav = useNavigate();
    const [acknowledged, setAcknowledged] = useState(false);

    const proceed = () => {
        try { localStorage.setItem(KEY, '1'); } catch (e) { console.debug('localStorage unavailable (private mode); disclosure not persisted', e); }
        const target = '/sender/home';
        nav(target, { replace: true });
    };

    return (
        <div className="nia-page">
            <div className="mobile-shell" style={{ background: 'var(--nia-cream)', color: 'var(--nia-ink)' }}>
                <div style={{ padding: '36px 22px 32px', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
                    <div
                        style={{
                            width: 60,
                            height: 60,
                            borderRadius: 18,
                            background: 'var(--nia-clay-tint)',
                            color: 'var(--nia-clay)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 22,
                        }}
                    >
                        <ShieldCheck size={26} />
                    </div>
                    <h1
                        className="font-display"
                        style={{
                            fontSize: 30,
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.15,
                            color: 'var(--nia-ink)',
                            margin: 0,
                        }}
                    >
                        Before you start — a quick promise from Nia.
                    </h1>
                    <div
                        style={{
                            color: 'var(--nia-text-muted)',
                            fontSize: 14,
                            marginTop: 14,
                            lineHeight: 1.6,
                        }}
                    >
                        Nia is a <b style={{ color: 'var(--nia-ink)' }}>planning &amp; coordination tool</b> for diaspora families. We help you plan support, track contributions, and stay in touch with family back home.
                    </div>

                    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {/* banned-terms:ok - explicit disclaimer bullets clarifying what Nia is NOT */}
                        <Bullet text="Nia is not a bank, lender, or money transfer service." />
                        <Bullet text="Nia does not hold or move your money. Ever." />
                        <Bullet text="All money flows directly between you and your chosen local M-Pesa or MoMo agent. Agents set their own fees." />
                        <Bullet text="&ldquo;Support requests&rdquo; are a planning tool. You coordinate the actual help directly with your local partner. Any amount, service fee, or reimbursement is agreed between you and that partner." />
                        <Bullet text="Nia keeps a record of what you plan and confirm — it never auto-transfers money for you." />
                    </div>

                    <label
                        style={{
                            marginTop: 28,
                            padding: 16,
                            background: 'var(--nia-sage-tint)',
                            border: '1px solid rgba(61, 107, 97, 0.22)',
                            borderRadius: 16,
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            data-testid="disclosure-ack"
                            style={{
                                width: 20,
                                height: 20,
                                accentColor: 'var(--nia-sage)',
                                marginTop: 2,
                                flexShrink: 0,
                            }}
                        />
                        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--nia-ink)' }}>
                            I understand that Nia is a coordination tool. Nia is not my bank, lender, or money transfer service. I will arrange the actual payment with my own local M-Pesa or MoMo agent.
                        </div>
                    </label>

                    <div style={{ flex: 1, minHeight: 12 }} />

                    <button
                        data-testid="disclosure-continue"
                        disabled={!acknowledged}
                        onClick={proceed}
                        className="nia-btn-primary"
                        style={{
                            marginTop: 24,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                        }}
                    >
                        I understand — continue <ArrowRight size={16} />
                    </button>

                    <div
                        style={{
                            marginTop: 12,
                            textAlign: 'center',
                            fontSize: 11,
                            color: 'var(--nia-text-subtle)',
                        }}
                    >
                        You can re-read this any time via Profile → About Nia.
                    </div>
                </div>
            </div>
        </div>
    );
}

function Bullet({ text }) {
    return (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: 'var(--nia-clay)',
                    marginTop: 8,
                    flexShrink: 0,
                }}
            />
            <div style={{ fontSize: 13, color: 'var(--nia-ink)', lineHeight: 1.6 }}>{text}</div>
        </div>
    );
}

export function shouldShowDisclosure() {
    try { return localStorage.getItem(KEY) !== '1'; } catch (e) { return false; }
}
